/**
 * bd fwk — Pure money-movement guards for ConfirmPanel.
 *
 * Lives at module scope so the predicate stack is testable without mounting
 * Preact or bootstrapping a risk session. ConfirmPanel calls these directly
 * to derive the pay button's enabled state and the consent payload it sends
 * with `confirmSession`.
 */

interface RiskLike {
  decision: 'ALLOW' | 'CHALLENGE' | 'STEP_UP_3DS' | 'BLOCK';
  possible_duplicate?: { matched_on_key: 'email' | 'phone'; delta_seconds: number };
}

/**
 * Derive the pay button's enabled flag from the four independent gates.
 * Returns true ONLY when every gate is satisfied — a single false anywhere
 * disables the button.
 */
export function isPayButtonEnabled(args: {
  allRequiredAccepted: boolean;
  submitting: boolean;
  risk: RiskLike | null;
  duplicateAcknowledged: boolean;
}): boolean {
  if (!args.allRequiredAccepted) return false;
  if (args.submitting) return false;
  if (!args.risk) return false;
  if (args.risk.decision === 'BLOCK') return false;
  if (args.risk.possible_duplicate && !args.duplicateAcknowledged) return false;
  return true;
}

interface ConsentLike {
  id: string;
  required: boolean;
  terms_version: string;
}

interface ConsentPayloadItem {
  id: string;
  termsVersion: string;
  acceptedAt: string;
}

/**
 * Build the consent payload sent with `confirmSession`. Includes ONLY the
 * consents the shopper has actually accepted; every emitted entry carries
 * the SAME acceptedAt timestamp (one click ⇒ one interaction).
 */
export function buildConsentPayload(
  consents: readonly ConsentLike[],
  accepted: Readonly<Record<string, boolean>>,
  nowIso: string,
): ConsentPayloadItem[] {
  return consents
    .filter((c) => accepted[c.id])
    .map((c) => ({
      id: c.id,
      termsVersion: c.terms_version,
      acceptedAt: nowIso,
    }));
}
